import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stethoscope, Loader2, Sparkles, RotateCcw, Search, X, Trophy } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { useLang } from "@/context/LanguageContext";

const STAGES = { SETUP: 0, CASE: 1, ASSESS: 2 };

export default function Cases() {
  const { t, lang } = useLang();
  const [stage, setStage] = useState(STAGES.SETUP);
  const [regions, setRegions] = useState([]);
  const [allTests, setAllTests] = useState([]);
  const [region, setRegion] = useState("slumpmassig");
  const [difficulty, setDifficulty] = useState("medel");
  const [loading, setLoading] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [assessment, setAssessment] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/regions"), api.get("/tests")])
      .then(([r, ts]) => { setRegions(r.data); setAllTests(ts.data); })
      .catch(() => toast.error(t("cases.errLoad")));
  }, [lang, t]);

  const generateCase = async () => {
    setLoading(true);
    setAssessment(null);
    try {
      const r = await api.post("/cases/generate", { region, difficulty });
      setCaseData(r.data);
      setSelected(new Set());
      setStage(STAGES.CASE);
    } catch (e) {
      toast.error(t("cases.errGen"));
    } finally {
      setLoading(false);
    }
  };

  const submitAssessment = async () => {
    if (selected.size === 0) return toast.warning(t("cases.selectOne"));
    setLoading(true);
    try {
      const r = await api.post("/cases/assess", {
        case_id: caseData.case_id,
        case_body: caseData.body,
        selected_test_ids: Array.from(selected),
        region: caseData.region,
      });
      setAssessment(r.data);
      setStage(STAGES.ASSESS);
    } catch (e) {
      toast.error(t("cases.errAssess"));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStage(STAGES.SETUP);
    setCaseData(null);
    setAssessment(null);
    setSelected(new Set());
  };

  const filteredTests = useMemo(() => {
    let pool = allTests;
    if (caseData?.region) {
      const reg = pool.filter((tst) => tst.region === caseData.region);
      const other = pool.filter((tst) => tst.region !== caseData.region);
      pool = [...reg, ...other];
    }
    if (filter) {
      const q = filter.toLowerCase();
      pool = pool.filter((tst) => tst.name.toLowerCase().includes(q) || tst.purpose.toLowerCase().includes(q));
    }
    return pool.slice(0, 80);
  }, [allTests, caseData, filter]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  // ---- SETUP ----
  if (stage === STAGES.SETUP) {
    return (
      <div data-testid="cases-setup" className="max-w-xl">
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
          <span className="title-rule" />{t("cases.eyebrow")}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight mb-3">
          {t("cases.title1")} <em className="text-accent italic font-serif">{t("cases.titleAccent")}</em>
        </h1>
        <p className="text-muted-foreground mb-8 leading-relaxed">
          {t("cases.lead")}
        </p>

        <Card className="p-6 space-y-5 bg-card">
          <div>
            <label className="text-sm font-medium mb-2 block">{t("cases.region")}</label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="case-region"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="slumpmassig">{t("cases.random")}</SelectItem>
                {regions.filter((r) => !["kranialnerver", "reflexer", "fullstandigt-ns"].includes(r.id)).map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">{t("cases.difficulty")}</label>
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger data-testid="case-difficulty"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="latt">{t("cases.diff.easy")}</SelectItem>
                <SelectItem value="medel">{t("cases.diff.medium")}</SelectItem>
                <SelectItem value="svar">{t("cases.diff.hard")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={generateCase} disabled={loading} className="w-full" data-testid="case-generate-btn">
            {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : <Sparkles className="size-4 mr-2" />}
            {t("cases.generate")}
          </Button>
        </Card>
      </div>
    );
  }

  // ---- CASE + TEST SELECTION ----
  if (stage === STAGES.CASE && caseData) {
    return (
      <div data-testid="cases-active" className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2 font-mono">
            <span className="title-rule" />{t("cases.case")}
            <Badge variant="secondary" className="ml-2 font-mono text-[10px]">{caseData.difficulty}</Badge>
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl font-medium mb-4 tracking-tight" data-testid="case-title">
            {caseData.title}
          </h2>
          <Card className="p-6 bg-card markdown-body text-sm" data-testid="case-body">
            <ReactMarkdown>{caseData.body}</ReactMarkdown>
          </Card>
          <button onClick={reset} className="text-xs text-muted-foreground hover:text-primary mt-3 flex items-center gap-1.5" data-testid="case-new-btn">
            <RotateCcw className="size-3" /> {t("cases.new")}
          </button>
        </div>

        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-24 space-y-4">
            <Card className="p-4 bg-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif text-lg font-medium">{t("cases.selected")}</h3>
                <Badge variant="secondary" className="font-mono" data-testid="case-selected-count">{selected.size}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                {t("cases.selectHint")}
              </p>

              <div className="relative mb-3">
                <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder={t("cases.searchTest")}
                  className="pl-9 text-sm"
                  data-testid="case-test-search"
                />
              </div>

              <div className="max-h-72 overflow-y-auto pr-1 space-y-1" data-testid="case-tests-list">
                {filteredTests.map((tst) => {
                  const checked = selected.has(tst.id);
                  const inRegion = tst.region === caseData.region;
                  return (
                    <label
                      key={tst.id}
                      className={`flex items-start gap-2.5 p-2 rounded cursor-pointer transition-colors border text-xs ${
                        checked ? "bg-accent/10 border-accent/40" : "border-transparent hover:bg-muted/50"
                      }`}
                      data-testid={`case-test-${tst.id}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggle(tst.id)} className="mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium leading-snug">{tst.name}</div>
                        <div className="text-muted-foreground line-clamp-1">{tst.purpose}</div>
                      </div>
                      {inRegion && <span className="text-[9px] font-mono text-accent shrink-0">REG</span>}
                    </label>
                  );
                })}
              </div>

              {selected.size > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5 pt-3 border-t border-border">
                  {Array.from(selected).map((id) => {
                    const tst = allTests.find((x) => x.id === id);
                    return (
                      <button
                        key={id}
                        onClick={() => toggle(id)}
                        className="text-[11px] bg-muted/60 rounded px-2 py-0.5 flex items-center gap-1 hover:bg-destructive/15"
                      >
                        {tst?.name} <X className="size-3" />
                      </button>
                    );
                  })}
                </div>
              )}

              <Button onClick={submitAssessment} disabled={loading || selected.size === 0} className="w-full mt-4" data-testid="case-submit-btn">
                {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : <Stethoscope className="size-4 mr-2" />}
                {t("cases.submit")}
              </Button>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ---- ASSESSMENT ----
  if (stage === STAGES.ASSESS && assessment) {
    const score = assessment.score;
    const color = score >= 80 ? "text-primary" : score >= 60 ? "text-accent" : "text-destructive";
    return (
      <div data-testid="cases-assessment" className="max-w-3xl">
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-mono">
          <span className="title-rule" />{t("cases.assessment")}
        </div>
        <div className="rise mb-6 flex items-end gap-5">
          <div className={`font-serif text-7xl font-medium ${color}`} data-testid="case-score">{score}</div>
          <div className="pb-3">
            <div className="text-sm text-muted-foreground uppercase tracking-widest font-mono">/ 100</div>
            <div className="font-serif text-xl">
              {score >= 80 ? t("cases.scoreExcellent") : score >= 60 ? t("cases.scoreSolid") : t("cases.scoreRepeat")}
              <Trophy className={`inline size-5 ml-2 ${color}`} />
            </div>
          </div>
        </div>

        <Card className="p-6 bg-card markdown-body text-sm rise" data-testid="case-feedback">
          <ReactMarkdown>{assessment.feedback}</ReactMarkdown>
        </Card>

        <div className="mt-6 flex gap-3">
          <Button onClick={reset} variant="outline" data-testid="case-restart-btn">
            <Sparkles className="size-4 mr-2" /> {t("cases.new")}
          </Button>
          <Button onClick={() => setStage(STAGES.CASE)} variant="secondary">
            {t("cases.backToCase")}
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
